// 순서 잇기(TMT) 전용 효과음
// - 외부 음원 없이 Web Audio 로 짧은 톤을 합성한다.
// - AudioContext 는 사용자 제스처(시작 버튼, 첫 터치) 이후에만 재생되고, 실패해도 게임 진행에는 영향이 없다.

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!audioCtx) {
    try {
      audioCtx = new Ctor();
    } catch {
      return null;
    }
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

// 시작 버튼 / 첫 터치에서 호출해 오디오 컨텍스트를 미리 깨워둔다.
export function unlockAudio(): void {
  getAudioContext();
}

interface ToneOptions {
  freq: number;
  duration: number;
  gain: number;
  type?: 'sine' | 'square' | 'triangle' | 'sawtooth';
  delay?: number;
  endFreq?: number;
}

function playTone({
  freq,
  duration,
  gain,
  type = 'sine',
  delay = 0,
  endFreq,
}: ToneOptions): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    const now = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    if (endFreq) {
      osc.frequency.exponentialRampToValueAtTime(endFreq, now + duration);
    }
    gainNode.gain.setValueAtTime(0.0001, now);
    gainNode.gain.exponentialRampToValueAtTime(gain, now + 0.012);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gainNode);
    gainNode.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + duration + 0.03);
  } catch {
    // 사운드 실패는 게임 진행에 영향을 주지 않는다
  }
}

// 선이 이어질 때: 짧게 위로 올라가는 "스윽" 소리
export function playLineSound(): void {
  playTone({ freq: 520, duration: 0.14, gain: 0.045, endFreq: 780, type: 'triangle' });
}

// 드래그가 노드에 붙었을 때: 아주 짧은 틱
export function playSnapSound(): void {
  playTone({ freq: 880, duration: 0.05, gain: 0.03, type: 'sine' });
}

// 정답 노드를 맞췄을 때: 밝은 두 음
export function playCorrectSound(): void {
  playTone({ freq: 783.99, duration: 0.09, gain: 0.05, type: 'sine' });
  playTone({ freq: 1046.5, duration: 0.14, gain: 0.045, delay: 0.08, type: 'sine' });
}

// 오답: 낮고 약한 피드백
export function playWrongSound(): void {
  playTone({ freq: 230, duration: 0.16, gain: 0.035, endFreq: 170, type: 'sine' });
}

// 단계 완성: 세 음 아르페지오
export function playCompleteSound(): void {
  playTone({ freq: 659.25, duration: 0.12, gain: 0.05, type: 'sine' });
  playTone({ freq: 830.61, duration: 0.12, gain: 0.05, delay: 0.1, type: 'sine' });
  playTone({ freq: 1046.5, duration: 0.2, gain: 0.05, delay: 0.2, type: 'sine' });
}